import { Component, OnDestroy, OnInit, SecurityContext, EventEmitter, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, tap, take } from 'rxjs/operators';
import { MicrofrontendUtils } from '@app/shared/mfe.utils';
import { AuthService } from '@app/shared/services/auth.service';
import { EventsService } from '@app/shared/services/events.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { environment } from '@env/environment';
import { PermissionService } from '@app/shared/services/api/permission.service';
import { MfeTeaserData } from '@app/teaser-main/models/mfe-teaser';
import { PermissionIdCode } from '@app/shared/models/permission-id-code';
import { isEmpty } from 'lodash';

@Component({
  selector: 'app-teaser-details',
  templateUrl: './teaser-details.component.html',
})
export class TeaserDetailsComponent implements OnInit, OnDestroy {
  @Output() closeTeaserPage = new EventEmitter();
  protected teaserSubscription$ = new Subscription();
  private url = new URL(environment.teaserUrl);
  private hasTeaserMfeLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private eventsService: EventsService,
    private overlayLoaderService: OverlayLoaderService,
    private permissionService: PermissionService,
    private sanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    let isLoaded = false;
    this.setupTeaserMfeLoadedEvent();
    this.teaserSubscription$ = this.permissionService.selectedPermission$
      .pipe(
        filter((selected: PermissionIdCode) => !!selected && !isEmpty(selected)),
        tap(() => (this.overlayLoaderService.isLoading = true)),
        map((selectedPermission: PermissionIdCode) => this.getMicroFrontEndData(isLoaded, selectedPermission)),
        switchMap((data: MfeTeaserData) => this.loadMFE(data)),
      )
      .subscribe({
        next: () => {
          isLoaded = true;
          addEventListener('closeTeaserPage', () => this.closeTeaserPage.emit());
        },
        error: () => (this.overlayLoaderService.isLoading = false),
      });
  }

  public ngOnDestroy(): void {
    this.hasTeaserMfeLoaded$.unsubscribe();
    this.teaserSubscription$.unsubscribe();
  }

  private getMicroFrontEndData(isLoaded: boolean, permission: PermissionIdCode): MfeTeaserData {
    const path = this.sanitizer.sanitize(
      SecurityContext.RESOURCE_URL,
      this.sanitizer.bypassSecurityTrustResourceUrl(MicrofrontendUtils.getUrl(this.url)),
    ) as string;
    return {
      microfrontend: {
        path: path,
        element: 'app-ep-mfe-teaser',
        parentElement: 'teaser-main-content',
        loaded: isLoaded,
      },
      permission: permission,
    };
  }

  private loadMFE(data: MfeTeaserData): Observable<any> {
    return MicrofrontendUtils.load(data.microfrontend).pipe(
      switchMap(() => this.hasTeaserMfeLoaded$),
      tap((hasMfeTeaserLoaded: boolean) => {
        if (hasMfeTeaserLoaded) {
          this.publishEvent(data);
          setTimeout(() => (this.overlayLoaderService.isLoading = false), 1000);
        }
      }),
    );
  }

  private publishEvent(data: MfeTeaserData): void {
    this.eventsService.publishEvent('permissionChangedEvent', {
      environment: environment,
      hasPermission: !!this.authService.permissions.TEASER_CONTENT_EDITOR && !!this.authService.permissions.GENERAL_CONFIGURATION,
      permission: data.permission,
      user: this.authService.currentUser,
    });
  }

  private setupTeaserMfeLoadedEvent(): void {
    fromEvent(window, 'teaserMfeLoaded')
      .pipe(
        take(1),
        tap(() => this.hasTeaserMfeLoaded$.next(true)),
      )
      .subscribe();
  }
}
